// src/components/EventForm/index.js
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormLabel,
  IconButton,
  Image,
  Input,
  Radio,
  RadioGroup,
  Stack,
  useToast,
} from "@chakra-ui/react";
import client from "../../utils/client";
import { v4 } from "uuid";
import { MdAddAPhoto } from "react-icons/md";
import { getImageUploadUrl } from "../../utils/s3";
import axios from "axios";

const EventForm = ({ event }) => {
  const [isEventOpen, setIsEventOpen] = useState(
    event ? String(event.isOpen) : "false"
  );
  const { register, handleSubmit } = useForm();
  const toast = useToast();
  const navigate = useNavigate();
  const fileInput = useRef(null);

  // Preview: either existing S3 URL or local object URL
  const [previewImage, setPreviewImage] = useState(
    event?.image
      ? `https://${process.env.REACT_APP_S3_BUCKET}.s3.${process.env.REACT_APP_S3_REGION}.amazonaws.com/events_images/${event.image}`
      : null
  );
  // Holds the File object selected by the user
  const [selectedFile, setSelectedFile] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  // When user picks a file: store it and set preview
  const handleImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  // Common submit handler for create & edit
  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      let imageKey = event?.image;

      if (selectedFile) {
        // 1) Gera nome e key únicas
        const filename = `eventImage-${v4()}.jpeg`;
        const key = `events_images/${filename}`;

        // 2) Pega signed URL do back via axios
        const signedUrl = await getImageUploadUrl(key, selectedFile.type);

        // 3) Faz upload no S3 com axios.put
        await axios.put(signedUrl, selectedFile, {
          headers: { "Content-Type": selectedFile.type },
        });

        imageKey = filename;
      }

      // 4) Monta o payload do evento
      const payload = {
        image: imageKey,
        name: data.name,
        theme: data.theme,
        eventDate: data.eventDate,
        eventStartSubscriptionForSpeakers:
          data.eventStartSubscriptionForSpeakers,
        eventEndSubscriptionForSpeakers: data.eventEndSubscriptionForSpeakers,
        eventStartSubscriptionForListeners:
          data.eventStartSubscriptionForListeners,
        eventEndSubscriptionForListeners: data.eventEndSubscriptionForListeners,
        isOpen: isEventOpen,
        observations: data.observations,
      };

      // 5) Chama seu back para criar ou editar
      if (event) {
        await client.patch(`/events/${event.eventId}`, payload);
        toast({
          title: "Evento alterado com sucesso",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
      } else {
        await client.post("/events", payload);
        toast({
          title: "Evento cadastrado com sucesso",
          description: "O evento foi cadastrado!",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
      }

      navigate("/allEvents");
    } catch (err) {
      console.error(err);
      toast({
        title: "Algum erro aconteceu",
        description: "Contate o Administrador do Sistema",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex flexDir="column" gap="5" my={5}>
        {/* Informações do Evento */}
        <Box border="1px solid #ccc" borderRadius="8px" p={[2, 5]} bg="white">
          <Badge fontSize="1rem" colorScheme="green" mb={2}>
            Informações do Evento
          </Badge>
          <Flex flexDir="column" gap={3}>
            <Stack direction={["column", "row"]}>
              <FormLabel w="100%" m={0}>
                Nome do Evento
                <Input
                  required
                  type="text"
                  placeholder="Nome do evento"
                  {...register("name")}
                  defaultValue={event?.name}
                />
              </FormLabel>
            </Stack>
            <Stack direction={["column", "row"]}>
              <FormLabel w="100%" m={0}>
                Tema do Evento
                <Input
                  required
                  type="text"
                  placeholder="Tema do evento"
                  {...register("theme")}
                  defaultValue={event?.theme}
                />
              </FormLabel>
            </Stack>
            <Stack direction={["column", "row"]}>
              <FormLabel w="100%" m={0}>
                Início inscrições Palestrantes
                <Input
                  required
                  type="date"
                  {...register("eventStartSubscriptionForSpeakers")}
                  defaultValue={event?.eventStartSubscriptionForSpeakers}
                />
              </FormLabel>
              <FormLabel w="100%" m={0}>
                Término inscrições Palestrantes
                <Input
                  required
                  type="date"
                  {...register("eventEndSubscriptionForSpeakers")}
                  defaultValue={event?.eventEndSubscriptionForSpeakers}
                />
              </FormLabel>
            </Stack>
            <Stack direction={["column", "row"]}>
              <FormLabel w="100%" m={0}>
                Início inscrições Ouvintes
                <Input
                  required
                  type="date"
                  {...register("eventStartSubscriptionForListeners")}
                  defaultValue={event?.eventStartSubscriptionForListeners}
                />
              </FormLabel>
              <FormLabel w="100%" m={0}>
                Término inscrições Ouvintes
                <Input
                  required
                  type="date"
                  {...register("eventEndSubscriptionForListeners")}
                  defaultValue={event?.eventEndSubscriptionForListeners}
                />
              </FormLabel>
            </Stack>
            <Stack direction={["column", "row"]}>
              <FormLabel w="100%" m={0}>
                Data do Evento
                <Input
                  required
                  type="date"
                  {...register("eventDate")}
                  defaultValue={event?.eventDate}
                />
              </FormLabel>
              <FormLabel w="100%" m={0}>
                Observações (Local evento presencial)
                <Input
                  required
                  type="text"
                  {...register("observations")}
                  defaultValue={event?.observations}
                />
              </FormLabel>
              <FormLabel w="100%" m={0}>
                Evento aberto ou fechado?
                <Flex p={2} alignItems="center">
                  <RadioGroup onChange={setIsEventOpen} value={isEventOpen}>
                    <Stack direction="row">
                      <Radio value="true">Aberto</Radio>
                      <Radio value="false">Fechado</Radio>
                    </Stack>
                  </RadioGroup>
                </Flex>
              </FormLabel>
            </Stack>
          </Flex>
        </Box>

        {/* Upload de Imagem */}
        <Box p={[2, 5]} bg="white" border="1px solid #ccc" borderRadius="8px">
          <Badge fontSize="1rem" colorScheme="green" mb={2}>
            Logo do Evento
          </Badge>
          <Flex
            flexDir={["column", "row"]}
            alignItems="center"
            justifyContent="space-between"
            gap={5}
          >
            <div>
              <Input
                type="file"
                accept="image/*"
                id="fileInput"
                display="none"
                onChange={handleImage}
                ref={fileInput}
              />
              <label htmlFor="fileInput">
                <Flex alignItems="center" gap={10}>
                  <FormLabel m={0}>Adicione a imagem do Evento</FormLabel>
                  <IconButton
                    colorScheme="green"
                    borderRadius="100%"
                    w={["70px", "85px"]}
                    h={["70px", "85px"]}
                    aria-label="Upload File"
                    icon={<MdAddAPhoto fontSize="45px" />}
                    onClick={() => fileInput.current.click()}
                  />
                </Flex>
              </label>
            </div>
            <Flex justifyContent="center" w="100%">
              {previewImage && <Image objectFit="cover" src={previewImage} />}
            </Flex>
          </Flex>
        </Box>

        {/* Botão de Envio */}
        <Flex justifyContent="center">
          <Button
            isLoading={isLoading}
            colorScheme="green"
            w={["100%", "40%"]}
            type="submit"
          >
            {event ? "Editar Evento" : "Cadastrar Evento"}
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};

export default EventForm;
